import React, { useState, useRef, useEffect } from "react";
import { useGesture } from "@use-gesture/react";
import { useSpring, animated } from "@react-spring/web";
import { FaExpand, FaMinus, FaPlus, FaTimes } from "react-icons/fa";

interface ZoomableImageProps {
  src: string;
  alt: string;
}

const ZoomableImage: React.FC<ZoomableImageProps> = ({ src, alt }) => {
  const [zoom, setZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [initialScale, setInitialScale] = useState(1);
  const imgRef = useRef<HTMLDivElement>(null);
  const imageElementRef = useRef<HTMLImageElement>(null);

  const [{ x, y, scale }, api] = useSpring(() => ({ x: 0, y: 0, scale: 1 }));

  const bind = useGesture(
    {
      onPinch: ({ offset: [d], memo = initialScale }) => {
        const newZoom = d * memo;
        if (newZoom < initialScale) {
          api.start({ scale: initialScale });
          setZoom(initialScale);
        } else {
          setZoom(newZoom);
          api.start({ scale: newZoom });
        }
        return memo;
      },
      onDrag: ({ offset: [dx, dy] }) => {
        if (zoom > initialScale) {
          const imgElement = imageElementRef.current;
          if (imgElement) {
            const { naturalWidth, naturalHeight } = imgElement;
            const maxOffsetX = Math.max(
              (naturalWidth * zoom - window.innerWidth) / 2,
              0
            );
            const maxOffsetY = Math.max(
              (naturalHeight * zoom - window.innerHeight) / 2,
              0
            );

            const clampedX = Math.min(Math.max(dx, -maxOffsetX), maxOffsetX);
            const clampedY = Math.min(Math.max(dy, -maxOffsetY), maxOffsetY);

            api.start({ x: clampedX, y: clampedY });
          }
        }
      },
    },
    {
      drag: { from: () => [x.get(), y.get()] },
      pinch: { scaleBounds: { min: initialScale, max: 10 } },
    }
  );

  const handleZoomIn = () => {
    const newZoom = Math.min(zoom * 1.2, 10);
    setZoom(newZoom);
    api.start({ scale: newZoom });
  };

  const handleZoomOut = () => {
    const newZoom = zoom / 1.2;
    if (newZoom < initialScale) {
      api.start({
        scale: initialScale,
      });
      setZoom(initialScale);
    } else {
      setZoom(newZoom);
      api.start({ scale: newZoom });
    }
  };

  const handleImageClick = () => {
    setIsFullscreen(true);
    document.body.style.overflow = "hidden";

    if (imageElementRef.current) {
      const { naturalWidth: imgWidth } = imageElementRef.current;
      const { innerWidth: screenWidth } = window;

      const fitScale = imgWidth > screenWidth ? screenWidth / imgWidth : 1;

      setInitialScale(fitScale);
      setZoom(fitScale);
      api.start({ scale: fitScale, x: 0, y: 0 });
    }
  };

  const handleCloseFullscreen = () => {
    setIsFullscreen(false);
    setZoom(1);
    setInitialScale(1);
    api.set({ x: 0, y: 0, scale: 1 });
    document.body.style.overflow = "auto";
  };

  useEffect(() => {
    if (!isFullscreen) {
      api.set({ x: 0, y: 0, scale: 1 });
      setZoom(1);
      setInitialScale(1);
    }
  }, [isFullscreen, api]);

  return (
    <>
      <div
        onClick={handleImageClick}
        className="relative overflow-hidden w-full md:w-3/4 lg:w-3/5 2xl:w-2/5 min-w-[90%] md:min-w-[75%] lg:min-w-[60%] 2xl:min-w-[40%] max-w-[90%] md:max-w-[75%] lg:max-w-[60%] 2xl:max-w-[40%]"
      >
        <FaExpand className="absolute text-white top-2 right-2 size-5 shadow-lg" />
        <img
          src={src}
          alt={alt}
          className="cursor-pointer"
          ref={imageElementRef}
          onDragStart={(e) => e.preventDefault()}
        />
      </div>

      {isFullscreen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex justify-center items-center">
          <animated.div
            {...bind()}
            ref={imgRef}
            style={{
              x,
              y,
              scale,
              touchAction: "none",
              cursor: "grab",
            }}
            className="w-full h-full flex justify-center items-center"
          >
            <img
              src={src}
              alt={alt}
              className="max-w-none max-h-none"
              onDragStart={(e) => e.preventDefault()}
            />
          </animated.div>
          <button
            onClick={handleCloseFullscreen}
            className="absolute top-4 right-4 bg-blue-gray-200 hover:bg-blue-gray-300 text-blue-gray-700 p-2 rounded-md shadow-lg"
          >
            <FaTimes />
          </button>
          <div className="absolute bottom-4 right-4 flex space-x-2">
            <button
              onClick={handleZoomOut}
              className="bg-blue-gray-200 hover:bg-blue-gray-300 text-blue-gray-700 p-2 rounded-md shadow-lg"
            >
              <FaMinus />
            </button>
            <button
              onClick={handleZoomIn}
              className="bg-blue-gray-200 hover:bg-blue-gray-300 text-blue-gray-700 p-2 rounded-md shadow-lg"
            >
              <FaPlus />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ZoomableImage;
